import {z} from 'zod';

export enum Partner {
    Asurion = 'asurion',
    ATT = 'att',
    Cox = 'cox',
    Cricket = 'cricket',
    DirecTV = 'directv',
    Koodo = 'koodo',
    Verizon = 'verizon',
    Telus = 'telus',
    TelusHomePro = 'telushomepro',
    USCellular = 'uscellular',
    Liberty = 'liberty',
}

export type Client = Partner;

export type Services = string[];

export enum ProductType {
    General = 'general',
    Home = 'home',
    Mobility = 'mobility',
    Appliance = 'appliance',
    Default = 'default',
}

export const PurchaseSchema = z.object({
    RetailName: z.string(),
    RetailSKU: z.string(),
    PurchaseDate: z.string(),
});

export type Purchase = z.infer<typeof PurchaseSchema>;

export const AgreementCustomerSchema = z.object({
    FirstName: z.string(),
    LastName: z.string(),
    Email: z.string().optional(),
});

export type AgreementCustomer = z.infer<typeof AgreementCustomerSchema>;

export const ClientAccountSchema = z.object({
    ClientChannelId: z.string(),
});

export type ClientAccount = z.infer<typeof ClientAccountSchema>;

export const AgreementSchema = z.object({
    Id: z.string(),
    ClientAccount: ClientAccountSchema,
    SubscriptionNumber: z.string(),
    Purchase: PurchaseSchema,
    Status: z.string(),
    Customers: z.array(AgreementCustomerSchema).optional(),
    TerminationDate: z.string().optional(),
});

export type Agreement = z.infer<typeof AgreementSchema>;

export const BasePlanDetailsSchema = z.object({
    id: z.string(),
    plan: z.string(),
    productType: z.nativeEnum(ProductType),
    displayName: z.string(),
});

export type BasePlanDetails = z.infer<typeof BasePlanDetailsSchema>;

export const PlanDetailsSchema = z.object({
    plan: BasePlanDetailsSchema,
    agreement: AgreementSchema,
});

export type PlanDetails = z.infer<typeof PlanDetailsSchema>;

export const VerifiedPlanDetailsSchema = PlanDetailsSchema.and(
    z.object({
        verifiedThisSession: z.boolean(),
    })
);

export type VerifiedPlanDetails = z.infer<typeof VerifiedPlanDetailsSchema>;

export const SubscriptionSchema = z.object({
    clientChannelId: z.string(),
    subscriptionNumber: z.string(),
});

export type Subscription = z.infer<typeof SubscriptionSchema>;

export const VerifiedSubscriptionSchema = SubscriptionSchema.and(
    z.object({
        verifiedThisSession: z.boolean(),
    })
);

export type VerifiedSubscription = z.infer<typeof VerifiedSubscriptionSchema>;

export const UserPlanDetailsSchema = z.intersection(
    PlanDetailsSchema,
    z.object({
        isVerified: z.boolean().optional(),
        isSelected: z.boolean().optional(),
    })
);

export type UserPlanDetails = z.infer<typeof UserPlanDetailsSchema>;

export const UserPlansServicesSchema = z.object({
    client: z.nativeEnum(Partner),
    services: z.string().array(),
    plans: UserPlanDetailsSchema.array(),
});

export const PartnersSchema = z.nativeEnum(Partner);

export type UserPlansServices = z.infer<typeof UserPlansServicesSchema>;
